import React from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Card.css';

/**
 * Card Component
 * A flexible container component with shadow, padding, and multiple variants
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to display inside the card
 * @param {string} props.variant - Visual variant: 'default', 'bordered', 'elevated'
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.hoverable - Enable hover effects
 * @param {Object} props.style - Inline styles
 */
const Card = ({
  children,
  variant = 'default',
  className = '',
  onClick,
  hoverable = false,
  style = {},
  ...props
}) => {
  const cardClasses = `
    card
    card--${variant}
    ${hoverable ? 'card--hoverable' : ''}
    ${onClick ? 'card--clickable' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const animationProps = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 },
    whileHover: hoverable ? { y: -4, transition: { duration: 0.2 } } : {},
  };

  return (
    <motion.div
      className={cardClasses}
      onClick={onClick}
      style={style}
      {...animationProps}
      {...props}
    >
      {children}
    </motion.div>
  );
};

Card.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'bordered', 'elevated']),
  className: PropTypes.string,
  onClick: PropTypes.func,
  hoverable: PropTypes.bool,
  style: PropTypes.object,
};

export default Card;
